/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Marcador de favorito POS (estrella teal) — tap dedicado; no agrega al carrito.
 */
import React, { memo, useEffect, useRef, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { Animated, Platform, Pressable, StyleSheet } from 'react-native';
import { ticketColors } from '../../../shared/theme/ticketColors';
import {
  animateFavoriteStarSubdued,
  runFavoriteMarkedPop,
  runFavoriteUnmarkedPulse,
  shouldReduceFavoriteMotion,
} from './posFavoriteStarMotion';

export type PosProductFavoriteStarProps = {
  isFavorite: boolean;
  onPress: () => void;
  /** `strip`: más chico, en fila con el título. */
  variant?: 'tile' | 'strip';
  /** Contorno visible cuando no está marcado. */
  showOutline?: boolean;
  /** Desktop: icono atenuado hasta hover en la ficha. */
  subdued?: boolean;
  disabled?: boolean;
  testID?: string;
  size?: number;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
};

function PosProductFavoriteStarInner({
  isFavorite,
  onPress,
  showOutline = true,
  subdued = false,
  disabled = false,
  testID,
  size: sizeProp,
  onHoverIn,
  onHoverOut,
  variant = 'tile',
}: PosProductFavoriteStarProps) {
  const size = sizeProp ?? (variant === 'strip' ? 16 : 18);
  const hitSize = variant === 'strip' ? 28 : 32;
  const scale = useRef(new Animated.Value(1)).current;
  const fillOpacity = useRef(new Animated.Value(isFavorite ? 1 : 0)).current;
  const outlineOpacity = useRef(new Animated.Value(isFavorite ? 0 : 1)).current;
  const subduedOpacity = useRef(new Animated.Value(subdued ? 0.38 : 1)).current;
  const favRef = useRef(isFavorite);
  const mountedRef = useRef(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    void shouldReduceFavoriteMotion().then(setReduceMotion);
  }, []);

  useEffect(() => {
    animateFavoriteStarSubdued(subduedOpacity, subdued, reduceMotion);
  }, [subdued, subduedOpacity, reduceMotion]);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      favRef.current = isFavorite;
      fillOpacity.setValue(isFavorite ? 1 : 0);
      outlineOpacity.setValue(isFavorite ? 0 : 1);
      return;
    }
    if (favRef.current === isFavorite) return;

    if (isFavorite) {
      runFavoriteMarkedPop(scale, reduceMotion);
      if (!reduceMotion) {
        Animated.parallel([
          Animated.timing(fillOpacity, { toValue: 1, duration: 120, useNativeDriver: true }),
          Animated.timing(outlineOpacity, { toValue: 0, duration: 100, useNativeDriver: true }),
        ]).start();
      } else {
        fillOpacity.setValue(1);
        outlineOpacity.setValue(0);
      }
    } else {
      runFavoriteUnmarkedPulse(scale, reduceMotion);
      if (!reduceMotion) {
        Animated.parallel([
          Animated.timing(fillOpacity, { toValue: 0, duration: 100, useNativeDriver: true }),
          Animated.timing(outlineOpacity, { toValue: 1, duration: 120, useNativeDriver: true }),
        ]).start();
      } else {
        fillOpacity.setValue(0);
        outlineOpacity.setValue(1);
      }
    }
    favRef.current = isFavorite;
  }, [fillOpacity, isFavorite, outlineOpacity, reduceMotion, scale]);

  if (!isFavorite && !showOutline) return null;

  return (
    <Pressable
      style={({ pressed }) => [
        styles.hit,
        { minWidth: hitSize, minHeight: hitSize },
        pressed && !disabled && styles.hitPressed,
      ]}
      onPress={(event) => {
        event.stopPropagation?.();
        onPress();
      }}
      disabled={disabled}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      hitSlop={10}
      accessibilityRole="button"
      accessibilityLabel={isFavorite ? 'Sacar de favoritos' : 'Marcar favorito'}
      testID={testID}
    >
      <Animated.View
        style={[
          styles.iconStack,
          {
            opacity: subduedOpacity,
            transform: [{ scale }],
          },
        ]}
      >
        <Animated.View style={[styles.iconLayer, { opacity: outlineOpacity }]}>
          <Ionicons name="star-outline" size={size} color={ticketColors.ink3} />
        </Animated.View>
        <Animated.View style={[styles.iconLayer, { opacity: fillOpacity }]}>
          <Ionicons name="star" size={size} color={ticketColors.brandInk} />
        </Animated.View>
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  hit: {
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as object) : null),
  },
  hitPressed: {
    opacity: 0.72,
  },
  iconStack: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconLayer: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export const PosProductFavoriteStar = memo(PosProductFavoriteStarInner);
